import React from "react";
import { Link } from "react-router-dom";
import { SignInButton } from "@clerk/clerk-react";
import { Authenticated, Unauthenticated } from "convex/react";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Sprout,
  Flame,
  Rocket,
  Users,
  Sparkles,
  FileText,
  Calendar,
  ClipboardList,
  BarChart3,
  Building2,
  Briefcase,
  MessageSquare,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Plan data matching PLAN_CONFIGS from convex/subscriptions.ts
const INDIVIDUAL_PLANS = [
  {
    id: "basic",
    name: "Basic",
    price: 0,
    priceDisplay: "Free",
    description: "Perfect for trying out ManageClean OS",
    credits: 0,
    icon: Sprout,
    color: "from-slate-50 to-slate-100",
    iconColor: "text-slate-600",
    iconBg: "bg-slate-100",
    features: [
      "1 user",
      "Unlimited bids",
      "Unlimited clients & leads",
      "Equipment & supplies",
      "Ratings & reviews",
      "Service projects",
      "Subcontractor management",
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    priceDisplay: "$29",
    description: "For office managers & small business owners",
    credits: 1000,
    icon: Flame,
    color: "from-blue-50 to-indigo-50",
    iconColor: "text-blue-600",
    iconBg: "bg-blue-100",
    popular: true,
    features: [
      "Everything in Basic",
      "1,000 credits/month",
      "5 Knowledge Base file uploads",
      "AI Business Assistant",
      "AI Document Writer",
      "AI generated Proposals",
      "AI generated Service Agreements",
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: 79,
    priceDisplay: "$79",
    description: "For operations directors & scaling companies",
    credits: 5000,
    icon: Rocket,
    color: "from-indigo-50 to-violet-50",
    iconColor: "text-indigo-600",
    iconBg: "bg-indigo-100",
    features: ["Everything in Starter", "5,000 credits/month", "50 Knowledge Base file uploads"],
  },
];

const TEAM_PLANS = [
  {
    id: "team10k",
    name: "Team 10k",
    price: 99,
    priceDisplay: "$99",
    description: "10,000 pooled credits",
    credits: 10000,
    features: [
      "Unlimited team members",
      "Team calendar",
      "100 Knowledge Base file uploads",
    ],
  },
  {
    id: "team25k",
    name: "Team 25k",
    price: 199,
    priceDisplay: "$199",
    description: "25,000 pooled credits",
    credits: 25000,
    popular: true,
    features: [
      "Unlimited team members",
      "Team calendar",
      "200 Knowledge Base file uploads",
    ],
  },
  {
    id: "team50k",
    name: "Team 50k",
    price: 349,
    priceDisplay: "$349",
    description: "50,000 pooled credits",
    credits: 50000,
    features: [
      "Unlimited team members",
      "Team calendar",
      "500 Knowledge Base file uploads",
    ],
  },
];

// Comprehensive feature list based on actual platform features
const FEATURE_CATEGORIES = [
  {
    name: "Agentic AI & Automation",
    icon: Sparkles,
    features: [
      { name: "Business Assistant AI", included: ["starter", "professional", "team"] },
      { name: "AI Document Writer", included: ["starter", "professional", "team"] },
    ],
  },
  {
    name: "Field Operations",
    icon: Zap,
    features: [
      { name: "Real-time Issue & Safety Reporting", included: ["team"] },
      {
        name: "Supply Inventory & Restock Alerts",
        included: ["basic", "starter", "professional", "team"],
      },
      { name: "Equipment Tracking", included: ["basic", "starter", "professional", "team"] },
      { name: "Service Projects", included: ["basic", "starter", "professional", "team"] },
    ],
  },
  {
    name: "Team & Workforce Management",
    icon: Users,
    features: [
      { name: "Employee Profiles", included: ["basic", "starter", "professional", "team"] },
      { name: "Role-based Permissions", included: ["basic", "starter", "professional", "team"] },
      { name: "Team Calendar & Scheduling", included: ["basic", "starter", "professional", "team"] },
      { name: "Unlimited Team Members", included: ["basic", "starter", "professional", "team"] },
      { name: "Subcontractor Management", included: ["basic", "starter", "professional", "team"] },
    ],
  },
  {
    name: "Lead & Bid Management",
    icon: Briefcase,
    features: [
      { name: "Lead Tracking & Pipeline", included: ["basic", "starter", "professional", "team"] },
      {
        name: "Bid Builder with Pricing Calculator",
        included: ["basic", "starter", "professional", "team"],
      },
      {
        name: "Margin & Workloading Analysis",
        included: ["basic", "starter", "professional", "team"],
      },
      {
        name: "Client Onboarding Pipelines",
        included: ["basic", "starter", "professional", "team"],
      },
    ],
  },
  {
    name: "Client Management",
    icon: ClipboardList,
    features: [
      { name: "Centralized CRM", included: ["basic", "starter", "professional", "team"] },
      { name: "Multi-location Support", included: ["basic", "starter", "professional", "team"] },
      {
        name: "Reviews & Reputation Management",
        included: ["basic", "starter", "professional", "team"],
      },
    ],
  },
  {
    name: "Analytics & Insights",
    icon: BarChart3,
    features: [
      { name: "Dashboard Overview", included: ["basic", "starter", "professional", "team"] },
      { name: "Advanced Performance Metrics", included: ["starter", "professional", "team"] },
      {
        name: "AI Usage & Credit Tracking",
        included: ["basic", "starter", "professional", "team"],
      },
    ],
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for ManageClean OS. Start free and scale as you grow. Individual and team plans available."
        keywords={[
          "pricing",
          "janitorial software pricing",
          "cleaning business software cost",
          "ManageClean plans",
        ]}
        url="/pricing"
        structuredData={[
          schemas.createProductSchema({
            name: "Basic",
            description: "Free plan for trying out ManageClean OS",
            price: 0,
          }),
          schemas.createProductSchema({
            name: "Starter",
            description: "For office managers & small business owners",
            price: 29,
          }),
          schemas.createProductSchema({
            name: "Professional",
            description: "For operations directors & scaling companies",
            price: 79,
          }),
          schemas.createProductSchema({
            name: "Team 10k",
            description: "10,000 pooled credits for teams",
            price: 99,
          }),
          schemas.createProductSchema({
            name: "Team 25k",
            description: "25,000 pooled credits for teams",
            price: 199,
          }),
          schemas.createProductSchema({
            name: "Team 50k",
            description: "50,000 pooled credits for teams",
            price: 349,
          }),
        ]}
      />
      <LandingNavbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden pb-16 pt-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.1),rgba(255,255,255,0))]" />
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600">
              <Sparkles className="h-4 w-4" />
              <span>Simple, transparent pricing</span>
            </div>
            <h1 className="mb-6 text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
              Start Your{" "}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Transformation
              </span>
            </h1>
            <p className="mx-auto max-w-2xl text-lg leading-relaxed text-slate-600 md:text-xl">
              Choose the plan that fits your business. All plans include our AI-powered assistant. Pay as you grow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Individual Plans */}
      <section className="bg-slate-50 py-12">
        <div className="container mx-auto px-4">
          <h2 className="mb-8 text-center text-2xl font-bold text-slate-900">Individual Plans</h2>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {INDIVIDUAL_PLANS.map((plan, index) => {
              const Icon = plan.icon;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={cn(
                    "relative rounded-2xl border bg-white shadow-sm transition-all duration-300 hover:shadow-xl",
                    plan.popular ? "scale-105 ring-2 ring-blue-500" : "border-slate-200"
                  )}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1 text-sm font-medium text-white">
                      Most Popular
                    </div>
                  )}
                  <div
                    className={cn(
                      "absolute inset-0 rounded-2xl bg-gradient-to-br opacity-50",
                      plan.color
                    )}
                  />
                  <div className="relative p-6">
                    <div className="mb-4 flex items-center gap-3">
                      <div className={cn("rounded-lg p-2", plan.iconBg)}>
                        <Icon className={cn("h-5 w-5", plan.iconColor)} />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
                    </div>

                    <div className="mb-4">
                      <span className="text-4xl font-bold text-slate-900">{plan.priceDisplay}</span>
                      {plan.price > 0 && <span className="text-slate-500">/month</span>}
                    </div>

                    <p className="mb-6 text-sm text-slate-600">{plan.description}</p>

                    <ul className="mb-6 space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className={cn("mt-0.5 h-4 w-4 flex-shrink-0", plan.iconColor)} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Unauthenticated>
                      <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
                        <Button
                          className={cn(
                            "w-full",
                            plan.popular ? "bg-blue-600 hover:bg-blue-700" : ""
                          )}
                          variant={plan.popular ? "default" : "outline"}
                        >
                          Get Started <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </SignInButton>
                    </Unauthenticated>
                    <Authenticated>
                      <Link to="/settings/billing">
                        <Button
                          className={cn(
                            "w-full",
                            plan.popular ? "bg-blue-600 hover:bg-blue-700" : ""
                          )}
                          variant={plan.popular ? "default" : "outline"}
                        >
                          Get Started Free <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </Authenticated>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Plans */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-10 text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-purple-100 bg-purple-50 px-3 py-1 text-sm font-medium text-purple-600">
              <Users className="h-4 w-4" />
              <span>Team Plans</span>
              <span className="ml-1 rounded border border-amber-500/30 bg-amber-500/20 px-1.5 py-0.5 text-xs font-medium text-amber-600">
                Beta
              </span>
            </div>
            <h2 className="mb-3 text-2xl font-bold text-slate-900 md:text-3xl">
              Scale Your Entire Team
            </h2>
            <p className="mx-auto max-w-2xl text-slate-600">
              Pooled credits for your field teams with unlimited users. Perfect for automated field
              operations.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-3">
            {TEAM_PLANS.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cn(
                  "relative rounded-2xl border bg-white p-6 transition-all hover:shadow-lg",
                  plan.popular ? "border-purple-200 ring-2 ring-purple-500" : "border-slate-200"
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-purple-600 px-3 py-0.5 text-xs font-medium text-white">
                    Best Value
                  </div>
                )}
                <div className="mb-3 flex items-center gap-2">
                  <div className="rounded-lg bg-purple-50 p-2">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                </div>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-slate-900">{plan.priceDisplay}</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <p className="mb-4 text-sm text-slate-600">{plan.description}</p>
                <ul className="mb-4 space-y-2 text-sm text-slate-600">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-purple-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Unauthenticated>
                  <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
                    <Button className="w-full" variant={plan.popular ? "default" : "outline"}>
                      Get Started
                    </Button>
                  </SignInButton>
                </Unauthenticated>
                <Authenticated>
                  <Link to="/settings/billing">
                    <Button className="w-full" variant={plan.popular ? "default" : "outline"}>
                      Get Started Free
                    </Button>
                  </Link>
                </Authenticated>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="bg-slate-50 py-16">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-2xl font-bold text-slate-900 md:text-3xl">
              Complete Feature List
            </h2>
            <p className="mx-auto max-w-2xl text-slate-600">
              Every plan includes full access to the ManageClean OS platform. Here's everything you
              get.
            </p>
          </div>

          <div className="mx-auto max-w-4xl space-y-6">
            {FEATURE_CATEGORIES.map((category) => {
              const CategoryIcon = category.icon;
              return (
                <div
                  key={category.name}
                  className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                >
                  <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-6 py-4">
                    <CategoryIcon className="h-5 w-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-900">{category.name}</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {category.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-6 py-3 transition-colors hover:bg-slate-50"
                      >
                        <span className="text-sm text-slate-700">{feature.name}</span>
                        <div className="flex items-center gap-4">
                          {feature.included.includes("basic") ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <span className="text-xs text-slate-400">Paid plans</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credit Top-Up Info */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-8 text-center">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100">
              <Zap className="h-6 w-6 text-amber-600" />
            </div>
            <h3 className="mb-2 text-xl font-bold text-slate-900">Need More Credits?</h3>
            <p className="mb-4 text-slate-600">
              Purchase Credit Top-Up Packs anytime. 5,000 credits for $20 — they never expire.
            </p>
            <p className="text-sm text-amber-700">Available after you create your account.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-900 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold text-white">Ready to Transform Your Business?</h2>
          <p className="mx-auto mb-8 max-w-xl text-slate-300">
            Join cleaning companies that are using ManageClean OS to streamline operations and grow
            faster.
          </p>
          <Unauthenticated>
            <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
              <Button
                size="lg"
                className="h-12 rounded-full bg-white px-8 text-lg text-slate-900 hover:bg-slate-100"
              >
                Start Free Today <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </SignInButton>
          </Unauthenticated>
          <Authenticated>
            <Link to="/dashboard">
              <Button
                size="lg"
                className="h-12 rounded-full bg-white px-8 text-lg text-slate-900 hover:bg-slate-100"
              >
                Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Authenticated>
        </div>
      </section>

      <Footer />
    </div>
  );
}
